<template>
  <modal
    id="guideline-detail-modal"
    ref="modal"
    :hide-footer="true"
    width="w-xxl"
  >
    <div slot="body">
      <div class="fg-detail-titlebar">
        <h5 class="fg-detail-title">{{ guideline.title }}</h5>
        <faq-guideline-new-badge v-if="guideline.is_new" />
      </div>
      <div
        v-if="publishedOn"
        class="fg-detail-published mb-2"
      >
        <i class="fa fa-calendar" /> Published on {{ publishedOn }}
      </div>
      <div
        v-if="categoryName"
        class="mb-1"
      >
        <span class="badge badge-info">{{ categoryName }}</span>
      </div>

      <p
        v-if="guideline.description"
        class="text-muted"
      >
        {{ guideline.description }}
      </p>

      <!-- FAQ -->
      <div
        v-if="guideline.type === 'faq'"
        id="fgFaqAccordion"
        class="accordion"
      >
        <div
          v-for="(faq, index) in faqContents"
          :key="index"
          class="card"
        >
          <div
            :id="'fgHeading' + index"
            :data-target="'#fgCollapse' + index"
            :aria-controls="'fgCollapse' + index"
            class="card-header fg-faq-header"
            data-toggle="collapse"
            aria-expanded="false"
          >
            <div>
              <strong>{{ faq.question }}</strong>
            </div>
          </div>
          <div
            :id="'fgCollapse' + index"
            :aria-labelledby="'fgHeading' + index"
            class="collapse"
            data-parent="#fgFaqAccordion"
          >
            <div class="card-body">
              {{ faq.answer }}
            </div>
          </div>
        </div>
        <div
          v-if="!faqContents.length"
          class="text-center text-muted mt-3"
        >
          No FAQ entries.
        </div>
      </div>

      <!-- Guideline -->
      <div 
        v-else
        class="ql-snow" 
      >
        <div
          v-if="content"
          class="guideline-content ql-editor"
          v-html="content"
        />

        <div
          v-if="files.length"
          class="mt-3"
        >
          <h6>Attachments</h6>
          <div
            v-for="(media, i) in files"
            :key="media.id"
            class="fg-viewer"
          >
            <div class="fg-viewer-head">
              <span class="fg-viewer-name">
                <i class="fa fa-file-o" />
                {{ attachmentLabel(media, i) }}
              </span>
              <!-- Streams through the app so Content-Disposition applies; a
                   direct link at the bucket URL is cross-origin, where the
                   download attribute is ignored and the browser navigates. -->
              <a
                v-if="media.is_downloadable"
                :href="downloadUrl(media)"
                class="btn white btn-sm"
              >
                <i class="fa fa-download" /> Download
              </a>
            </div>

            <!-- PDF: native inline viewer (toolbar hidden when not downloadable) -->
            <iframe
              v-if="ext(media) === 'pdf'"
              :src="pdfSrc(media)"
              class="fg-viewer-frame"
            />
            <!-- Plain text: rendered inline -->
            <iframe
              v-else-if="ext(media) === 'txt'"
              :src="media.original"
              class="fg-viewer-frame"
            />
            <!-- Slides: Office Online embed (needs a public, internet-reachable URL) -->
            <iframe
              v-else-if="isOffice(media)"
              :src="officeUrl(media.original)"
              class="fg-viewer-frame"
            />
            <!-- Video: inline player with session resume -->
            <video
              v-else-if="isVideo(media)"
              :src="media.original"
              controls
              controlsList="nodownload"
              class="fg-viewer-video"
              @loadedmetadata="restoreVideo(media, $event)"
              @timeupdate="saveVideo(media, $event)"
              @contextmenu.prevent
            />
            <!-- Other (e.g. txt): no inline viewer -->
            <p
              v-else
              class="text-muted fg-viewer-note"
            >
              Preview not available for this file type.
            </p>
          </div>
        </div>
      </div>

      <div class="fg-detail-footer mt-3">
        <div
          v-if="canAcknowledge"
          class="fg-ack"
        >
          <span
            v-if="guideline.is_acknowledged"
            class="fg-ack-status"
          >
            <i class="fa fa-check-circle" /> Read on {{ acknowledgedOn }}
          </span>
          <template v-else>
            <span
              v-if="guideline.is_updated_since_ack"
              class="fg-ack-updated"
            >Updated since you last read</span>
            <button
              :disabled="acknowledging"
              type="button"
              class="btn primary btn-fw"
              @click="acknowledge"
            >
              {{ acknowledging ? 'Saving...' : 'Mark as read' }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import FaqGuidelineNewBadge from './faq-guideline-new-badge.vue';

const Laroute = require('../../laroute');

const MODAL_ID = 'guideline-detail-modal';

export default {
    components: { FaqGuidelineNewBadge },

    props: {
        showAcknowledge: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            guideline: {},
            acknowledging: false,
        };
    },

    computed: {
        canAcknowledge() {
            return this.showAcknowledge && this.guideline.type === 'guideline';
        },
        acknowledgedOn() {
            return this.guideline.acknowledged_at
                ? moment(this.guideline.acknowledged_at).format('DD MMM YYYY')
                : '';
        },
        publishedOn() {
            return this.guideline.published_at
                ? moment(this.guideline.published_at).format('DD MMM YYYY')
                : '';
        },
        categoryName() {
            return this.guideline.category
                ? this.guideline.category.name
                : null;
        },
        faqContents() {
            return this.guideline.faq_contents || [];
        },
        content() {
            return this.guideline.guideline_content
                ? this.guideline.guideline_content.content
                : '';
        },
        files() {
            return (this.guideline.media_urls || []).filter(
                m => m.collection_name === 'faq_guideline_files',
            );
        },
    },

    mounted() {
        // The modal can be dismissed by the close button, the backdrop or ESC —
        // not only via hide() — so hook Bootstrap's own event rather than a
        // component-level one, otherwise audio keeps playing behind the page.
        this.onModalHidden = () => this.stopMedia();
        $(`#${MODAL_ID}`).on('hidden.bs.modal', this.onModalHidden);
    },

    beforeDestroy() {
        $(`#${MODAL_ID}`).off('hidden.bs.modal', this.onModalHidden);
    },

    methods: {
        show(guideline) {
            this.guideline = guideline;
            this.$refs.modal.$emit('show');
        },

        // Pause every player in the modal. The sessionStorage position that
        // saveVideo() recorded is left alone, so reopening still resumes.
        stopMedia() {
            const modal = document.getElementById(MODAL_ID);

            if (!modal) {
                return;
            }

            modal.querySelectorAll('video').forEach(video => video.pause());
        },

        downloadUrl(media) {
            return Laroute.route('franchise.faq-guideline.media.download', {
                id: this.guideline.id,
                mediaId: media.id,
            });
        },

        acknowledge() {
            this.acknowledging = true;
            this.$store
                .dispatch(
                    'faqGuideline/acknowledgeFaqGuideline',
                    this.guideline.id,
                )
                .then(guideline => {
                    this.guideline = guideline;
                    // Hand the fresh record up so the list can update the card
                    // immediately, rather than relying on a refetch landing
                    // before the user looks at it.
                    this.$emit('acknowledged', guideline);
                })
                .catch(() => {
                    this.swalError({ text: 'Unable to mark as read.' });
                })
                .finally(() => {
                    this.acknowledging = false;
                });
        },

        ext(media) {
            return (media.original || '')
                .split('.')
                .pop()
                .toLowerCase();
        },

        attachmentLabel(media, index) {
            return `Attachments #${index + 1}.${this.ext(media)}`;
        },

        // Hide the browser PDF toolbar (download/print) for locked files.
        // #toolbar=0 is honoured by Chromium; Firefox/Safari ignore it.
        pdfSrc(media) {
            return media.is_downloadable
                ? media.original
                : `${media.original}#toolbar=0`;
        },

        isOffice(media) {
            return ['ppt', 'pptx'].includes(this.ext(media));
        },

        isVideo(media) {
            return ['mp4', 'mov'].includes(this.ext(media));
        },

        officeUrl(url) {
            return (
                'https://view.officeapps.live.com/op/embed.aspx?src=' +
                encodeURIComponent(url)
            );
        },

        // Resume playback from where the dealer left off in the same session.
        restoreVideo(media, event) {
            const saved = sessionStorage.getItem(`fgVideo:${media.id}`);
            if (saved) {
                event.target.currentTime = parseFloat(saved);
            }
        },

        saveVideo(media, event) {
            sessionStorage.setItem(
                `fgVideo:${media.id}`,
                event.target.currentTime,
            );
        },
    },
};
</script>

<style>
#guideline-detail-modal .modal-dialog {
    max-width: 820px;
}
/* Unscoped so it reaches v-html-injected guideline content images. */
#guideline-detail-modal .guideline-content img {
    max-width: 100%;
    height: auto;
}
</style>

<style scoped>
.fg-detail-titlebar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
}
.fg-detail-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #2b3543;
}

/* Sits on its own line under the category badge. */
.fg-detail-published {
    font-size: 12px;
    color: #8a94a6;
}
.fg-detail-published .fa {
    margin-right: 4px;
}

.fg-detail-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
}
.fg-ack {
    display: flex;
    align-items: center;
    gap: 10px;
}
.fg-ack-status {
    color: #2a9d8f;
    font-size: 13px;
    font-weight: 600;
}
.fg-ack-updated {
    color: #c77700;
    font-size: 13px;
    font-weight: 600;
}

.guideline-content {
    padding: 0;
    min-height: unset;
    margin-bottom: 10px;
}

.fg-viewer {
    margin-bottom: 18px;
}
.fg-viewer-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 6px;
}
.fg-viewer-name {
    font-size: 13px;
    font-weight: 600;
    color: #2b3543;
}
.fg-viewer-name .fa {
    margin-right: 6px;
    color: #8a94a6;
}
.fg-viewer-frame {
    width: 100%;
    height: 480px;
    border: 1px solid #edf0f2;
    border-radius: 8px;
}
.fg-viewer-video {
    width: 100%;
    max-height: 480px;
    border-radius: 8px;
    background: #000;
}
.fg-viewer-note {
    font-size: 13px;
    margin: 0;
}

#fgFaqAccordion .card {
    margin-bottom: 10px;
    border: 1px solid #e9edf0;
    border-radius: 8px;
    overflow: hidden;
}
#fgFaqAccordion .card-header {
    background: #f6f8f9;
    border-bottom: 0;
}
.fg-faq-header {
    cursor: pointer;
    padding: 14px 18px;
}
.fg-faq-header h2 {
    font-size: 15px;
    font-weight: 600;
    color: #2b3543;
}
#fgFaqAccordion .card-body {
    padding: 14px 18px;
    font-size: 14px;
    line-height: 1.6;
    color: #647285;
    border-top: 1px solid #eef1f3;
}

.fg-file-list {
    list-style: none;
    padding: 0;
    margin: 0;
}
.fg-file-list li {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border: 1px solid #edf0f2;
    border-radius: 8px;
    margin-bottom: 8px;
}
.fg-file-link {
    color: #2a9d8f;
    font-size: 14px;
    text-decoration: none;
}
.fg-file-link:hover {
    text-decoration: underline;
}
.fg-file-link .fa {
    margin-right: 6px;
    color: #8a94a6;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/faq_guideline/faq-guideline-detail.vue